import apiClient from "./axiosConfig";

/**
 * Price list (pricing catalog) APIs — BASE_URL includes /api prefix.
 *
 * GET    /products/price-lists/
 * GET    /products/price-lists/:id/
 * GET    /products/price-lists/full/
 * POST   /products/price-lists/              { name, index }
 * PATCH  /products/price-lists/:id/          { add_products } | { remove_product_ids } | { index } | { name, index } | { product_indexes }
 * DELETE /products/price-lists/:id/
 */

const PRICE_LISTS = "/products/price-lists";

const extractList = (payload) => {
  const root = payload?.data ?? payload;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.results)) return root.results;
  if (Array.isArray(root?.price_lists)) return root.price_lists;
  if (Array.isArray(root?.groups)) return root.groups;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const unwrap = (data) => data?.data ?? data;

const toIndexString = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
};

/** Canonical grid index string: 62 | "62" | "062" → "62". */
const toCanonicalGridIndex = (value) => {
  const raw = toIndexString(value);
  if (!raw || !/^\d+$/.test(raw) || raw.length < 2) return raw;
  const col = Number(raw.slice(-1));
  const row = Number(raw.slice(0, -1));
  if (col >= 1 && col <= 3 && row >= 1) return `${row}${col}`;
  return raw;
};

/** Sort key for string grid indexes ("11","62") — last digit = col, rest = row. */
const getIndexing = (item) => {
  const canonical = toCanonicalGridIndex(
    item.index ?? item.indexing ?? item.order ?? item.position ?? item.sort_order
  );
  if (!canonical || canonical.length < 2) return 0;
  const col = Number(canonical.slice(-1));
  const row = Number(canonical.slice(0, -1));
  if (col >= 1 && col <= 3 && row >= 1) return row * 10 + col;
  const num = Number(canonical);
  return Number.isFinite(num) ? num : 0;
};

const sortByIndexing = (list) =>
  [...list].sort((a, b) => getIndexing(a) - getIndexing(b));

/** GET all price list groups (metadata / order). */
export const getPriceLists = async () => {
  const response = await apiClient.get(`${PRICE_LISTS}/`);
  return sortByIndexing(extractList(response.data));
};

/** GET single price list group with products. */
export const getPriceListDetail = async (groupId) => {
  const response = await apiClient.get(`${PRICE_LISTS}/${groupId}/`);
  return unwrap(response.data);
};

/** GET all groups with nested products (preferred for initial load). */
export const getPriceListsFull = async () => {
  const response = await apiClient.get(`${PRICE_LISTS}/full/`);
  return sortByIndexing(extractList(response.data));
};

/** POST create price list group. */
export const createPriceList = async ({ name, index }) => {
  const canonical = toCanonicalGridIndex(index);
  const response = await apiClient.post(`${PRICE_LISTS}/`, {
    name,
    // Group indexes must be grid strings ("11", "62"), never "1"
    index: canonical && canonical.length >= 2 ? canonical : "11",
  });
  return unwrap(response.data);
};

/** PATCH price list — add products, update index, and/or name. Returns full group. */
export const patchPriceList = async (groupId, body = {}) => {
  const payload = { ...body };
  if (payload.index != null) {
    payload.index = toCanonicalGridIndex(payload.index) ?? String(payload.index).trim();
  }
  const response = await apiClient.patch(`${PRICE_LISTS}/${groupId}/`, payload);
  return unwrap(response.data);
};

/** PATCH add inventory products to a price list group with indexes. */
export const addProductsToPriceList = async (groupId, products = []) => {
  const addProducts = products
    .filter((product) => product?.product_id != null)
    .map((product) => ({
      product_id: product.product_id,
      index: Number(product.index),
    }));

  if (!addProducts.length) {
    throw new Error("At least one product id is required");
  }
  return patchPriceList(groupId, { add_products: addProducts });
};

/** PATCH remove products from a price list group. */
export const removeProductsFromPriceList = async (groupId, productIds = []) => {
  const ids = productIds.filter((id) => id != null);
  if (!ids.length) {
    throw new Error("At least one product id is required");
  }
  return patchPriceList(groupId, { remove_product_ids: ids });
};

/** PATCH group name and/or index. */
export const updatePriceList = async (groupId, { name, index } = {}) => {
  const payload = {};
  if (name != null) payload.name = name;
  if (index != null) {
    payload.index = toCanonicalGridIndex(index) ?? String(index).trim();
  }
  return patchPriceList(groupId, payload);
};

/** DELETE price list group (soft delete). */
export const deletePriceList = async (groupId) => {
  const response = await apiClient.delete(`${PRICE_LISTS}/${groupId}/`);
  return unwrap(response.data);
};

/** After drag: PATCH groups whose packed visual slot index changed. */
export const updatePricingCatalogGroupOrder = async (
  groups = [],
  previousGroups = []
) => {
  const prevIndexById = new Map(
    previousGroups.map((group) => [
      String(group.id),
      toCanonicalGridIndex(group.index ?? group.indexing),
    ])
  );

  const changed = groups
    .map((group) => ({
      id: group.id,
      index: toCanonicalGridIndex(group.index ?? group.indexing),
    }))
    .filter(({ id, index }) => id != null && index && index.length >= 2)
    .filter(({ id, index }) => prevIndexById.get(String(id)) !== index);

  if (!changed.length) return [];

  return Promise.all(
    changed.map(({ id, index }) => patchPriceList(id, { index }))
  );
};

/** After product drag: PATCH product indexes for the changed rows in one group. */
export const updatePricingCatalogProductOrder = async (
  groupId,
  products = [],
  previousProducts = []
) => {
  const prevIndexById = new Map(
    previousProducts.map((product, i) => [
      String(product.id),
      String(product.index ?? product.indexing ?? i + 1),
    ])
  );

  const changed = products
    .map((product, i) => ({
      id: product.id,
      product_id: product.productId ?? product.product_id ?? product.id,
      index: String(i + 1),
    }))
    .filter(({ id, index }) => prevIndexById.get(String(id)) !== index);

  if (!groupId || !changed.length) return [];

  return patchPriceList(groupId, { product_indexes: changed });
};

/**
 * Load catalog for UI: uses /full/ (one request).
 * Fallback: /price-lists/ then GET each group by id.
 */
export const fetchPricingCatalogGroups = async () => {
  try {
    const full = await getPriceListsFull();
    if (full.length) return full;
  } catch (fullError) {
    console.warn("Price lists /full/ unavailable, falling back to per-group fetch:", fullError);
  }

  const orderList = await getPriceLists();
  if (!orderList.length) return [];

  const details = await Promise.all(
    orderList.map((item) => {
      const id = item.id ?? item.group_id ?? item.price_list_id;
      return getPriceListDetail(id);
    })
  );
  return details;
};

export const getPricingCatalogGroupOrder = getPriceLists;
export const getPricingCatalogGroupDetail = getPriceListDetail;
